<template>
  <v-dialog v-model="dialog" max-width="600px" persistent>
    <v-card>
      <v-card-title class="headline">
        Negociar Parcelas
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert type="info" text dense class="mb-4">
          {{ selectedInstallments.length }} parcela(s) selecionada(s) - 
          Total: {{ totalDebt | currency }}
        </v-alert>

        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.down_payment"
                label="Valor da Entrada"
                prefix="R$"
                type="number"
                step="0.01"
                min="0"
                :rules="[rules.required, rules.minValue]"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.down_payment_date"
                label="Data da Entrada"
                v-mask="'##/##/####'"
                placeholder="DD/MM/AAAA"
                :rules="[rules.required, rules.date]"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.installments_count"
                label="Número de Parcelas"
                type="number"
                min="1"
                max="60"
                :rules="[rules.required, rules.minInstallments, rules.maxInstallments]"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.first_installment_date"
                label="Data da 1ª Parcela"
                v-mask="'##/##/####'"
                placeholder="DD/MM/AAAA"
                :rules="[rules.required, rules.date]"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Preview do Acordo -->
          <v-card outlined class="mt-3" v-if="previewData">
            <v-card-title class="subtitle-2">Resumo do Acordo</v-card-title>
            <v-card-text>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td><strong>Total da Dívida:</strong></td>
                    <td class="text-right">{{ totalDebt | currency }}</td>
                  </tr>
                  <tr>
                    <td><strong>Entrada:</strong></td>
                    <td class="text-right">{{ formData.down_payment | currency }}</td>
                  </tr>
                  <tr>
                    <td><strong>Saldo a Parcelar:</strong></td>
                    <td class="text-right">{{ remainingAmount | currency }}</td>
                  </tr>
                  <tr>
                    <td><strong>Valor da Parcela:</strong></td>
                    <td class="text-right">{{ installmentValue | currency }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancelar</v-btn>
        <v-btn 
          color="primary" 
          @click="submit" 
          :loading="loading"
          :disabled="!valid"
        >
          Confirmar Negociação
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/services/index.js';
import { errorsToString } from '@/helpers/index.js';

export default {
  name: 'RenegotiateFinancialModal',
  
  props: {
    value: Boolean,
    selectedInstallments: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      valid: false,
      loading: false,
      formData: {
        down_payment: 0,
        down_payment_date: '',
        installments_count: 1,
        first_installment_date: ''
      },
      rules: {
        required: v => !!v || v === 0 || 'Campo obrigatório',
        minValue: v => parseFloat(v) >= 0 || 'Valor não pode ser negativo',
        minInstallments: v => parseInt(v) >= 1 || 'Mínimo de 1 parcela',
        maxInstallments: v => parseInt(v) <= 60 || 'Máximo de 60 parcelas',
        date: v => {
          if (!v) return 'Data obrigatória';
          const regex = /^\d{2}\/\d{2}\/\d{4}$/;
          return regex.test(v) || 'Formato inválido (DD/MM/AAAA)';
        }
      }
    };
  },

  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },

    totalDebt() {
      return this.selectedInstallments.reduce((sum, item) => {
        const amount = item.total != null ? item.total : item.value;
        return sum + parseFloat(amount || 0);
      }, 0);
    },

    remainingAmount() {
      return this.totalDebt - parseFloat(this.formData.down_payment || 0);
    },

    installmentValue() {
      const count = parseInt(this.formData.installments_count) || 1;
      return this.remainingAmount / count;
    },

    previewData() {
      return this.formData.installments_count > 0;
    }
  },

  watch: {
    value(val) {
      if (val) {
        this.setDefaultDates();
      } else {
        this.resetForm();
      }
    }
  },

  methods: {
    setDefaultDates() {
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 15);
      
      this.formData.down_payment_date = this.formatDate(today);
      this.formData.first_installment_date = this.formatDate(nextMonth);
    },

    formatDate(date) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },

    async submit() {
      if (!this.$refs.form.validate()) {
        Toast.fire('Preencha todos os campos corretamente', '', 'warning');
        return;
      }

      const result = await this.$swal({
        title: 'Confirmar Negociação?',
        html: `
          <p><strong>${this.selectedInstallments.length} parcela(s)</strong> serão marcadas como "Negociação"</p>
          <p>Total: <strong>${this.$options.filters.currency(this.totalDebt)}</strong></p>
          <p>Entrada: <strong>${this.$options.filters.currency(this.formData.down_payment)}</strong></p>
          <p><strong>${this.formData.installments_count} nova(s) parcela(s)</strong> de 
          <strong>${this.$options.filters.currency(this.installmentValue)}</strong></p>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, negociar',
        cancelButtonText: 'Cancelar'
      });

      if (!result.isConfirmed) return;

      this.loading = true;

      try {
      const payload = {
        financial_ids: this.selectedInstallments.map(item => item.id),
        down_payment: parseFloat(this.formData.down_payment),
        down_payment_date: this.formData.down_payment_date,
        installments_count: parseInt(this.formData.installments_count),
        first_installment_date: this.formData.first_installment_date
      };

      const { data } = await api.post('/financials/renegotiate', payload);

      Toast.fire(data.message, '', 'success');

      // ⭐ NOVO: Download automático da minuta de acordo
      if (data.data?.agreement_draft_url) {
        this.downloadAgreementDraft(data.data.agreement_draft_url);
      }

      this.$emit('success', data);
      this.close();

  } catch (error) {
    console.error('Erro ao negociar parcelas:', error);
    
    if (error.response?.data?.errors) {
      Toast.fire(errorsToString(error.response.data.errors), '', 'error');
    } else if (error.response?.data?.message) {
      Toast.fire(error.response.data.message, '', 'error');
    } else {
      Toast.fire('Erro ao processar negociação', '', 'error');
    }
  } finally {
    this.loading = false;
  }
    },

    // ⭐ NOVO MÉTODO: Download da minuta
    downloadAgreementDraft(url) {
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = `minuta-acordo-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    close() {
      this.dialog = false;
    },

    resetForm() {
      this.$refs.form?.resetValidation();
      this.formData = {
        down_payment: 0,
        down_payment_date: '',
        installments_count: 1,
        first_installment_date: ''
      };
    }
  }
};
</script>

<style scoped>
.v-simple-table tbody tr:last-child {
  font-weight: bold;
  background-color: #f5f5f5;
}
</style>
